<template>
  <section class="office-format-cards">
    <h2 class="section-title">{{ $t('office.formatCards.title') }}</h2>
    <div class="format-cards-grid">
      <div v-for="(card, index) in formatCards" :key="card.name" class="format-card">
        <div class="card-image">
          <img :src="card.imageUrl" :alt="card.displayName" />
        </div>
        <div class="card-content">
          <div class="card-header">
            <Icon :name="card.icon" class="card-icon" />
            <h3 class="card-title">{{ card.displayName }}</h3>
          </div>
          <div class="card-info">
            <div><strong>{{ $t('office.formatCards.labels.supported') }}</strong>{{ card.extensions.join(', ') }}</div>
            <div><strong>{{ $t('office.formatCards.labels.preserve') }}</strong>{{ card.preservedFeatures.join('、') }}</div>
            <div><strong>{{ $t('office.formatCards.labels.use') }}</strong>{{ card.useCases.join('、') }}</div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const formatCards = computed(() => [
  {
    name: 'word',
    displayName: t('office.formatCards.word.name'),
    icon: 'vscode-icons:file-type-word',
    imageUrl: '/images/office/word-format-card.jpg',
    extensions: ['.docx', '.doc'],
    preservedFeatures: [
      t('office.formatCards.word.preserved.0'),
      t('office.formatCards.word.preserved.1'),
      t('office.formatCards.word.preserved.2')
    ],
    useCases: [
      t('office.formatCards.word.useCases.0'),
      t('office.formatCards.word.useCases.1'),
      t('office.formatCards.word.useCases.2')
    ]
  },
  {
    name: 'powerpoint',
    displayName: t('office.formatCards.ppt.name'),
    icon: 'vscode-icons:file-type-powerpoint',
    imageUrl: '/images/office/ppt-format-card.jpg',
    extensions: ['.pptx', '.ppt'],
    preservedFeatures: [
      t('office.formatCards.ppt.preserved.0'),
      t('office.formatCards.ppt.preserved.1'),
      t('office.formatCards.ppt.preserved.2')
    ],
    useCases: [
      t('office.formatCards.ppt.useCases.0'),
      t('office.formatCards.ppt.useCases.1'),
      t('office.formatCards.ppt.useCases.2')
    ]
  },
  {
    name: 'excel',
    displayName: t('office.formatCards.excel.name'),
    icon: 'vscode-icons:file-type-excel',
    imageUrl: '/images/office/excel-format-card.jpg',
    extensions: ['.xlsx', '.xls'],
    preservedFeatures: [
      t('office.formatCards.excel.preserved.0'),
      t('office.formatCards.excel.preserved.1'),
      t('office.formatCards.excel.preserved.2')
    ],
    useCases: [
      t('office.formatCards.excel.useCases.0'),
      t('office.formatCards.excel.useCases.1'),
      t('office.formatCards.excel.useCases.2')
    ]
  }
])
</script>

<style scoped>
.office-format-cards {
  padding: 4rem 0;
}

.section-title {
  text-align: center;
  font-size: 2rem;
  color: #2c3e50;
  margin-bottom: 3rem;
}

.format-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  max-width: 1000px;
  margin: 0 auto;
}

.format-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  transition: transform 0.3s ease;
}

.format-card:hover {
  transform: translateY(-8px);
}

.card-image {
  height: 200px;
  overflow: hidden;
}

.card-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.format-card:hover .card-image img {
  transform: scale(1.05);
}

.card-header {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
}

.card-icon {
  font-size: 2rem;
  margin-right: 0.75rem;
}

.card-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
}

.card-info > div {
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  line-height: 1.4;
}
</style>
